import { useCallback, useMemo, useState } from 'react';
import { SxStyle } from '../../../../../types';
import { useStringsContext } from '../../../../../contexts/strings';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { QuizPoll, PlayerPollOption, QuizResult } from '../../types';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { usePoll, usePollResults } from '../../hooks/poll';
import { useQuizResults } from '../../hooks/useQuizResults';
import Link from '@mui/material/Link';
import Collapse from '@mui/material/Collapse';
import PollCard from '../poll-card';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import DownloadIcon from '@mui/icons-material/Download';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import PollVotesModal from '../modals/poll-votes-modal';
import PollQuizDetailsModal from '../modals/poll-quiz-details-modal';
import PollPushSegmentModal from '../modals/poll-push-segment-modal';
import { useSnackbar } from 'notistack';

type PollAndResult = {
  count: number;
  percentage: number;
} & PlayerPollOption;

type QuizAndResult = {
  score: number;
  count: number;
  percentage: number;
  items: QuizResult[];
};

const styles: SxStyle = {
  listItem: (theme) => ({
    borderBottom: `1px solid ${theme.palette.grey[300]}`,

    '&:last-child': {
      borderBottom: 0,
    },
  }),
  listItemText: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px',
  },
};

interface QuizResultsListItemProps {
  item: QuizAndResult;
}

interface PollResultsListItemProps {
  item: PollAndResult;
}

const QuizResultsListItem = ({ item }: QuizResultsListItemProps) => {
  const { strings } = useStringsContext();

  return (
    <ListItem key={item.score} sx={styles.listItem} disablePadding>
      <Stack direction="row" sx={styles.listItemText}>
        <ListItemText
          primaryTypographyProps={{ variant: 'body2' }}
          primary={strings.polls.quizResultRow.replace('$score', String(item.score))}
        />
        <ListItemText
          sx={{ flexGrow: 0 }}
          primaryTypographyProps={{ variant: 'body2' }}
          primary={strings.polls.pollResultRow
            .replace('$percentage', String(item.percentage))
            .replace('$count', String(item.count))
            .replace(
              '$votes',
              item.count === 1
                ? strings.polls.pollResultVoteSingle
                : strings.polls.pollResultVoteMany
            )}
        />
      </Stack>
    </ListItem>
  );
};

const PollResultsListItem = ({ item }: PollResultsListItemProps) => {
  const { strings } = useStringsContext();

  return (
    <ListItem key={item.id} sx={styles.listItem} disablePadding>
      <Stack direction="row" sx={styles.listItemText}>
        <ListItemText
          primaryTypographyProps={{ variant: 'body2' }}
          primary={`${item.text || item.value}`}
        />
        <ListItemText
          sx={{ flexGrow: 0 }}
          primaryTypographyProps={{ variant: 'body2' }}
          primary={strings.polls.pollResultRow
            .replace('$percentage', String(item.percentage))
            .replace('$count', String(item.count))
            .replace(
              '$votes',
              item.count === 1
                ? strings.polls.pollResultVoteSingle
                : strings.polls.pollResultVoteMany
            )}
        />
      </Stack>
    </ListItem>
  );
};

interface Props {
  selectedPoll: QuizPoll | null;
}

const PollResultsCard = ({ selectedPoll }: Props) => {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();
  const { getVotersCSV } = usePoll();
  const [showMore, setShowMore] = useState(false);
  const [showVotesModal, setShowVotesModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showPushSegmentModal, setShowPushSegmentModal] = useState(false);

  const isQuiz = selectedPoll?.type === 'quiz';

  // Use declarative hooks for fetching results
  const {
    data: pollResults,
    isLoading: isPollLoading,
    error: pollError,
    refetch: refetchPollResults,
  } = usePollResults(isQuiz ? null : (selectedPoll?.id ?? null), {
    includePollOptions: true,
  });

  const {
    data: quizResults,
    isLoading: isQuizLoading,
    error: quizError,
    refetch: refetchQuizResults,
  } = useQuizResults(isQuiz ? (selectedPoll?.id ?? null) : null);

  const isLoading = isQuiz ? isQuizLoading : isPollLoading;
  const hasError = isQuiz ? !!quizError : !!pollError;
  const refetch = isQuiz ? refetchQuizResults : refetchPollResults;

  // Process poll results
  const { totalVotes: pollTotalVotes, pollsAndResults } = useMemo(() => {
    if (!pollResults?.pollOptions || !pollResults?.result) {
      return { totalVotes: 0, pollsAndResults: [] };
    }

    const options = pollResults.pollOptions;
    const results = pollResults.result;

    if (options.length === 0 || results.length === 0) {
      return { totalVotes: 0, pollsAndResults: [] };
    }

    const numTotalVotes = results.reduce((acc, cur) => acc + cur.count, 0);
    const pollAndResults = options
      .map((p) => {
        const count = results.find((result) => result.id === p.id)?.count || 0;
        const percentage = numTotalVotes > 0 ? Math.round((count / numTotalVotes) * 100) : 0;
        return {
          ...p,
          count,
          percentage,
        } satisfies PollAndResult;
      })
      .sort((a, b) => b.count - a.count);

    return { totalVotes: numTotalVotes, pollsAndResults: pollAndResults };
  }, [pollResults]);

  // Process quiz results
  const { totalVotes: quizTotalVotes, quizAndResults } = useMemo(() => {
    if (!quizResults || quizResults.length === 0) {
      return { totalVotes: 0, quizAndResults: [] };
    }

    const participants = quizResults.length;
    const scores = Array.from(new Set(quizResults.map((r) => r.score)));
    const summary = scores
      .sort((a, b) => b - a)
      .map((score) => {
        const items = quizResults.filter((r) => r.score === score);
        const count = items.length;
        const percentage = Math.round((count / participants) * 100);
        return {
          score,
          count,
          percentage,
          items,
        };
      });

    return { totalVotes: participants, quizAndResults: summary };
  }, [quizResults]);

  const totalVotes = isQuiz ? quizTotalVotes : pollTotalVotes;

  const onExportCSV = useCallback(async () => {
    if (!selectedPoll) return;

    setIsExporting(true);

    try {
      const csvUrl = await getVotersCSV(selectedPoll.id, selectedPoll.type === 'quiz');

      if (csvUrl) {
        const a = document.createElement('a');
        a.href = csvUrl;
        a.download = 'export.csv';
        a.click();
        URL.revokeObjectURL(csvUrl);
      }

      enqueueSnackbar(strings.messages.exportSuccess, {
        variant: 'success',
      });
    } catch {
      // Error is already handled in usePoll hook with snackbar notification
    } finally {
      setIsExporting(false);
    }
  }, [getVotersCSV, selectedPoll, enqueueSnackbar, strings]);

  // Empty state when no poll is selected
  if (!selectedPoll) {
    return (
      <PollCard title={strings.polls.pollResultTitle}>
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {strings.polls.fieldNameName}
          </Typography>
        </Box>
      </PollCard>
    );
  }

  // Error state with retry option
  if (hasError && !isLoading) {
    return (
      <PollCard title={strings.polls.pollResultTitle}>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
            >
              {strings.actions.ok}
            </Button>
          }
        >
          {strings.messages.loadingError}
        </Alert>
      </PollCard>
    );
  }

  return (
    <>
      <PollCard title={strings.polls.pollResultTitle}>
        <Stack direction="row" justifyContent="space-between">
          <Box>
            <Typography variant="body2-bold">{strings.polls.pollResultVotes}</Typography>
            {isLoading ? (
              <Skeleton variant="text" width={60} height={40} />
            ) : (
              <Typography variant="h5">{totalVotes}</Typography>
            )}
          </Box>
          <Box>
            <Stack direction="column" gap={1}>
              <Button
                startIcon={<FormatListNumberedIcon />}
                variant="outlined"
                onClick={() => setShowVotesModal(true)}
                disabled={isLoading || totalVotes === 0}
              >
                {strings.polls.showVotes}
              </Button>
              <Button
                startIcon={<DownloadIcon />}
                variant="outlined"
                onClick={onExportCSV}
                loading={isExporting}
                disabled={isLoading || totalVotes === 0}
              >
                {strings.polls.downloadVotes}
              </Button>
              <Button
                startIcon={<GroupAddIcon />}
                variant="outlined"
                onClick={() => setShowPushSegmentModal(true)}
                disabled={isLoading || totalVotes === 0}
              >
                {strings.polls.exportToPushGroup}
              </Button>
            </Stack>
          </Box>
        </Stack>

        {/* Loading state for results list */}
        {isLoading && (
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="text" width={100} height={24} sx={{ mb: 1 }} />
            <Stack spacing={1}>
              <Skeleton variant="rectangular" height={40} />
              <Skeleton variant="rectangular" height={40} />
              <Skeleton variant="rectangular" height={40} />
            </Stack>
          </Box>
        )}

        {/* Poll results list */}
        {!isLoading && pollsAndResults.length > 0 && (
          <Box>
            <Typography variant="body2-bold">{strings.polls.pollResultList}</Typography>
            <List>
              {pollsAndResults.slice(0, 3).map((p) => (
                <PollResultsListItem key={p.id} item={p} />
              ))}
              <Collapse in={showMore}>
                {pollsAndResults.slice(3).map((p) => (
                  <PollResultsListItem key={p.id} item={p} />
                ))}
              </Collapse>
            </List>
            {pollsAndResults.length > 3 && (
              <Box>
                <Link
                  sx={{ textDecoration: 'underline' }}
                  component="button"
                  variant="body2"
                  onClick={() => setShowMore(!showMore)}
                >
                  {showMore ? strings.actions.hide : strings.actions.show}
                </Link>
              </Box>
            )}
          </Box>
        )}

        {/* Quiz results list */}
        {!isLoading && quizAndResults.length > 0 && (
          <Box>
            <Typography variant="body2-bold">{strings.polls.quizResultList}</Typography>
            <List>
              {quizAndResults.slice(0, 3).map((p, i) => (
                <QuizResultsListItem key={i} item={p} />
              ))}
              <Collapse in={showMore}>
                {quizAndResults.slice(3).map((p, i) => (
                  <QuizResultsListItem key={i} item={p} />
                ))}
              </Collapse>
            </List>
            {quizAndResults.length > 3 && (
              <Box>
                <Link
                  sx={{ textDecoration: 'underline' }}
                  component="button"
                  variant="body2"
                  onClick={() => setShowMore(!showMore)}
                >
                  {showMore ? strings.actions.hide : strings.actions.show}
                </Link>
              </Box>
            )}
          </Box>
        )}

        {/* Empty results state */}
        {!isLoading && totalVotes === 0 && (
          <Box sx={{ mt: 2, textAlign: 'center', py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {strings.polls.pollResultVotes}: 0
            </Typography>
          </Box>
        )}
      </PollCard>
      {showVotesModal &&
        (isQuiz ? (
          <PollQuizDetailsModal
            open={showVotesModal}
            items={quizAndResults.map((q) => q.items).flat()}
            onClose={() => setShowVotesModal(false)}
          />
        ) : (
          <PollVotesModal
            open={showVotesModal}
            selectedPoll={selectedPoll}
            onClose={() => setShowVotesModal(false)}
          />
        ))}
      {showPushSegmentModal && (
        <PollPushSegmentModal
          open={showPushSegmentModal}
          selectedPoll={selectedPoll}
          onClose={() => setShowPushSegmentModal(false)}
        />
      )}
    </>
  );
};

export default PollResultsCard;
